import apiFetch from '../../../utils/apiFetch';

const pending = new Map();
const requests = new Map();
const keyFor = value => JSON.stringify(value);
async function json(response) {
  const data = await response.json();
  if (!response.ok) throw Object.assign(new Error(data.message || 'Spell activation failed.'), { status: response.status });
  return data;
}

// A failed/uncertain request keeps its identity so retrying cannot spend twice.
export async function activateManualSpell(character, spell, turnSequence) {
  const id = character._id;
  if (!id) throw new Error('Save this character before casting.');
  if (requests.has(id)) throw new Error('A spell activation is already in progress.');
  requests.set(id, true);
  try {
    let access, resolvedSpells;
    if (!spell.featureGrantId) {
      const matches = (character.spells || []).map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => spell.spellInstanceId ? entry?.spellInstanceId === spell.spellInstanceId
          : (typeof entry === 'string' ? entry : entry?.name) === (spell.name || spell.spellName))
        .filter(({ entry }) => !spell.sourceId || !entry?.source || entry.source.sourceId === spell.sourceId.toLowerCase());
      if (matches.length !== 1) throw new Error('Choose this spell’s casting class in the spellbook before casting.');
      access = matches[0].entry;
      if (!access?.spellInstanceId) {
        const upgraded = await json(await apiFetch(`/characters/${id}/spells/${matches[0].index}/resolve-source`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(spell.sourceId ? { sourceId: spell.sourceId.toLowerCase() } : {}),
        }));
        access = upgraded.spell; resolvedSpells = upgraded.spells;
      }
    }
    const baseLevel = Number(access?.level ?? spell.baseLevel ?? spell.level);
    const mode = spell.mode || (baseLevel === 0 ? 'cantrip' : 'slot');
    const pool = mode === 'slot' ? ({ regular: 'spellcasting', warlock: 'pactMagic', spellcasting: 'spellcasting', pactMagic: 'pactMagic' }[spell.slotType]) : 'none';
    if (!pool) throw new Error('Choose a Spellcasting or Pact Magic slot first.');
    const intent = { ...(spell.featureGrantId ? { featureGrantId: spell.featureGrantId } : { spellInstanceId: access.spellInstanceId }),
      mode, pool, castLevel: mode === 'slot' ? Number(spell.slotLevel ?? spell.level) : baseLevel,
      ...(Number.isInteger(turnSequence) ? { turnSequence } : {}) };
    const costIdentity = { ...intent };
    delete costIdentity.turnSequence;
    const fingerprint = keyFor(costIdentity);
    let retry = pending.get(id);
    if (retry && retry.fingerprint !== fingerprint) throw new Error('Retry the previous spell activation first; its result is uncertain.');
    if (!retry) {
      retry = { fingerprint, intent, resolutionId: (typeof window !== 'undefined' ? window.crypto?.randomUUID?.() : null) || `manual:${Date.now()}:${Math.random()}` };
      pending.set(id, retry);
    }
    try {
      const result = await json(await apiFetch(`/characters/${id}/manual-spell-casts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...retry.intent, resolutionId: retry.resolutionId }),
      }));
      pending.delete(id);
      return { ...result, ...(resolvedSpells ? { spells: resolvedSpells } : {}) };
    } catch (error) {
      if (error.status >= 400 && error.status < 500) pending.delete(id);
      throw error;
    }
  } finally { requests.delete(id); }
}
